import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'
import { ArrowLeft } from 'lucide-react'
import {
  fetchStaatidAlderFordeling,
  fetchStaatidForhandlere,
  fetchStaatidForhandlereTrend,
  fetchStaatidInnsikt,
  fetchStaatidMerker,
  fetchStaatidOversikt,
  loadScatterSample,
  monthRange,
  fetchStaatidRoundtrips,
  formatDager,
  formatAndel,
  formatBenchmarkDelta,
  UNKNOWN_ERROR,
} from './api'
import type {
  StaatidParams,
  StaatidAlderFordelingResponse,
  StaatidForhandlerRow,
  StaatidInnsiktResponse,
  StaatidMerkerResponse,
  StaatidOversiktResponse,
  StaatidRoundtripsResponse,
  StaatidTrendPoint,
} from './api'
import AlderFordeling from './AlderFordeling'
import InnsiktPanel from './InnsiktPanel'
import MerkeScatter from './MerkeScatter'
import MonthlyTrendChart from './MonthlyTrendChart'
import TrailPanel from './TrailPanel'
import SubTabBar from '../SubTabBar'

// One section per tab (#656). Local state, not the URL: the dealer itself is
// already deep-linked (?forhandler=), and reopening a dealer starts on the
// first tab.
const DETALJ_TABS = [
  { key: 'enkeltsalg', labelKey: 'detalj.tabs.enkeltsalg' },
  { key: 'maanedstrend', labelKey: 'detalj.tabs.maanedstrend' },
  { key: 'bilalder', labelKey: 'detalj.tabs.bilalder' },
  { key: 'merker', labelKey: 'detalj.tabs.merker' },
  { key: 'innsikt', labelKey: 'detalj.tabs.innsikt' },
] as const

type DetaljTab = (typeof DETALJ_TABS)[number]['key']

interface Props {
  orgNr: string
  /** Shared period/geo filters from the layout (no dealer set). */
  baseFilters: StaatidParams
  onBack: () => void
}

/**
 * One dealer's ståtid: header stats above five tabs — scatter of the dealer's
 * individual sales colored per merke with per-merke monthly median lines, the
 * monthly trend, ståtid by car age, a per-merke table, and Innsikt (#662).
 *
 * The first four sections load together up front, so switching between them
 * sends no requests. Innsikt (#662) is the exception: it loads the first time
 * its tab is opened, and once per filter set after that.
 */
export default function ForhandlerDetalj({ orgNr, baseFilters, onBack }: Props) {
  const { t } = useTranslation('staatid')
  const [stats, setStats] = useState<StaatidForhandlerRow | null>(null)
  const [merker, setMerker] = useState<StaatidMerkerResponse | null>(null)
  const [oversikt, setOversikt] = useState<StaatidOversiktResponse | null>(null)
  // Market-wide oversikt for the same period/geo/merke slice, minus the dealer
  // filter — powers the benchmark delta (#274 Fase 3, Part A).
  const [marketOversikt, setMarketOversikt] = useState<StaatidOversiktResponse | null>(null)
  const [scatter, setScatter] = useState<StaatidRoundtripsResponse | null>(null)
  const [alder, setAlder] = useState<StaatidAlderFordelingResponse | null>(null)
  // This dealer's own monthly points (#529) — from /forhandlere/trend rather
  // than /oversikt so the series is dealer-scoped at the source.
  const [maanedstrend, setMaanedstrend] = useState<StaatidTrendPoint[]>([])
  const [zoomMonth, setZoomMonth] = useState<string | null>(null)
  const [trailSluttSvvId, setTrailSluttSvvId] = useState<number | null>(null)
  const [tab, setTab] = useState<DetaljTab>('enkeltsalg')
  const [innsikt, setInnsikt] = useState<StaatidInnsiktResponse | null>(null)
  // The filterKey `innsikt` was loaded for; a mismatch means it is stale.
  const [innsiktKey, setInnsiktKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const maxDataDate = useMaxDataDate()

  const filters: StaatidParams = { ...baseFilters, dealerOrgNr: [orgNr] }
  const filterKey = `${orgNr}|${baseFilters.from}|${baseFilters.to}|${baseFilters.selgerPostnummer ?? ''}`

  useLatest(alive => {
    setLoading(true)
    setError(null)
    setZoomMonth(null)
    Promise.all([
      fetchStaatidForhandlere(filters, 'antall_roundtrips', 'desc', 1, 0),
      fetchStaatidMerker(filters, [], 500, 0),
      fetchStaatidOversikt(filters),
      fetchStaatidOversikt(baseFilters),
      loadScatterSample(filters),
      fetchStaatidAlderFordeling(filters),
      fetchStaatidForhandlereTrend(filters, [orgNr]),
    ])
      .then(([f, m, o, mo, r, a, t]) => {
        if (!alive()) return
        setStats(f.data.rows[0] ?? null)
        setMerker(m.data)
        setOversikt(o.data)
        setMarketOversikt(mo.data)
        setScatter(r)
        setAlder(a.data)
        setMaanedstrend(t.data.rows[0]?.trend ?? [])
      })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [filterKey])

  // Lazy (#662 AC1): no request until the tab is open. The key is null on the
  // other tabs, so switching away and back to the same filters re-fires
  // nothing — the guard below sees the result is already for this filterKey.
  const innsiktWanted = tab === 'innsikt' ? filterKey : null
  useLatest(alive => {
    if (innsiktWanted === null || innsiktWanted === innsiktKey) return
    setInnsikt(null)
    fetchStaatidInnsikt(filters)
      .then(r => { if (alive()) { setInnsikt(r.data); setInnsiktKey(innsiktWanted) } })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
  }, [innsiktWanted])

  const benchmarkDelta = formatBenchmarkDelta(
    stats?.median_dager ?? null, marketOversikt?.median_dager ?? null,
  )

  function handleZoom(month: string | null) {
    setZoomMonth(month)
    const load = month
      ? fetchStaatidRoundtrips({ ...filters, ...monthRange(month) }, 2000, 0).then(r => r.data)
      : loadScatterSample(filters)
    load
      .then(setScatter)
      .catch(e => setError(e instanceof Error ? e.message : UNKNOWN_ERROR))
  }

  if (error) {
    return (
      <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
        {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="staatid-forhandler-detalj">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="staatid-detalj-back"
            className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {stats?.dealer_org_navn ?? orgNr}
            </h3>
            <p className="font-mono text-xs text-primary-600 dark:text-primary-400">{orgNr}</p>
          </div>
        </div>
        {stats && (
          <div className="flex flex-wrap gap-6 text-sm">
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.roundtrips')}</span> <strong>{stats.antall_roundtrips.toLocaleString('nb-NO')}</strong></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.median')}</span> <strong>{formatDager(stats.median_dager)} d</strong></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.snitt')}</span> <strong>{formatDager(stats.snitt_dager)} d</strong></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('detalj.medianEgne')}</span> <strong>{formatDager(stats.median_siste_ledd_dager)} d</strong></div>
            <div data-testid="staatid-detalj-kommisjon" title={t('common.kommisjonForklaring')}>
              <span className="text-gray-500 dark:text-gray-400">{t('common.stat.kommisjonssalg')}</span>{' '}
              <strong>{stats.antall_kommisjon.toLocaleString('nb-NO')} ({formatAndel(stats.andel_kommisjon)})</strong>
            </div>
            {marketOversikt && (
              <div><span className="text-gray-500 dark:text-gray-400">{t('detalj.marked')}</span> <strong>{formatDager(marketOversikt.median_dager)} d</strong></div>
            )}
            {benchmarkDelta && (
              <div data-testid="staatid-benchmark-delta">
                <span className="text-gray-500 dark:text-gray-400">{t('detalj.vsMarked')}</span>{' '}
                <strong className="text-primary-600 dark:text-primary-400">{benchmarkDelta}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      <SubTabBar
        parent={t('common.dealer')}
        tabs={DETALJ_TABS.map(({ key, labelKey }) => ({ key, label: t(labelKey) }))}
        active={tab}
        onChange={setTab}
        testIdPrefix="staatid-detalj-tab-"
      />

      {tab === 'enkeltsalg' && (
        <>
          <MerkeScatter
            rows={scatter?.rows ?? []}
            total={scatter?.total ?? 0}
            merker={merker?.rows ?? []}
            combinedTrend={oversikt?.trend ?? []}
            combinedStats={oversikt ? {
              antall: oversikt.antall_parede,
              median_dager: oversikt.median_dager,
              snitt_dager: oversikt.snitt_dager,
            } : undefined}
            loading={loading}
            zoomMonth={zoomMonth}
            onZoomMonth={handleZoom}
            onPointClick={setTrailSluttSvvId}
            // #655: the numbers live in the hover tooltip on this page.
            showTable={false}
          />

          <TrailPanel sluttSvvId={trailSluttSvvId} onClose={() => setTrailSluttSvvId(null)} />
        </>
      )}

      {tab === 'maanedstrend' && (
        <MonthlyTrendChart
          trend={maanedstrend}
          from={baseFilters.from}
          to={baseFilters.to}
          loading={loading}
          scope={stats?.dealer_org_navn ?? orgNr}
          maxDataDate={maxDataDate}
          showTable={false}
        />
      )}

      {tab === 'bilalder' && (
        <AlderFordeling
          rows={alder?.rows ?? []}
          total={alder?.total ?? 0}
          utenAlder={alder?.uten_alder ?? 0}
          loading={loading}
          scope={stats?.dealer_org_navn ?? orgNr}
        />
      )}

      {tab === 'merker' && (
        <div className="card overflow-hidden" data-testid="staatid-detalj-merker">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('detalj.merkerTitle')}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
                <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.merke')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
              </tr>
            </thead>
            <tbody>
              {(merker?.rows ?? []).map((row, i) => (
                <tr key={row.merke ?? i} className={`border-b border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}>
                  <td className="px-4 py-2">{row.merke ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
                </tr>
              ))}
              {!loading && (merker?.rows ?? []).length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">{t('common.noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'innsikt' && (
        <>
          <InnsiktPanel
            data={innsiktKey === filterKey ? innsikt : null}
            loading={innsiktKey !== filterKey}
            onOpenTrail={setTrailSluttSvvId}
          />
          <TrailPanel sluttSvvId={trailSluttSvvId} onClose={() => setTrailSluttSvvId(null)} />
        </>
      )}
    </div>
  )
}
